import { api } from '../../lib/api';
import type {
  Job,
  JobWithRelations,
  JobTask,
  JobTaskWithRelations,
  JobNoteWithRelations,
  JobTaskNoteWithRelations,
  JobChangeHistoryWithRelations,
  JobTaskChangeHistoryWithRelations,
  JobQuery,
  JobTaskQuery,
  JobNotesQuery,
  JobHistoryQuery,
  CreateJobInput,
  UpdateJobInput,
  UpdateJobTaskInput,
  CreateJobNoteInput,
  CreateJobTaskNoteInput,
  AddTasksToJobInput,
} from '@maker-toolchest/shared';

// Helper to build query strings
function buildQueryString(params?: Record<string, any>): string {
  if (!params) return '';
  const entries = Object.entries(params).filter(([_, v]) => v != null);
  if (entries.length === 0) return '';
  const searchParams = new URLSearchParams();
  entries.forEach(([k, v]) => searchParams.append(k, String(v)));
  return `?${searchParams.toString()}`;
}

// ============================================================================
// JOBS
// ============================================================================

export async function listJobs(query: JobQuery) {
  const response = await api.get<Job[]>(`/jobs${buildQueryString(query)}`);

  if (!response.data) {
    throw new Error('No data returned from API');
  }

  // Return full response with data and meta
  return { data: response.data, meta: response.meta };
}

export async function createJob(data: CreateJobInput) {
  const response = await api.post<JobWithRelations>('/jobs', data);

  if (!response.data) {
    throw new Error('No data returned from API');
  }

  return response.data;
}

export async function getJob(id: string) {
  const response = await api.get<JobWithRelations>(`/jobs/${id}`);

  if (!response.data) {
    throw new Error('No data returned from API');
  }

  return response.data;
}

export async function updateJob(id: string, data: UpdateJobInput) {
  const response = await api.patch<Job>(`/jobs/${id}`, data);

  if (!response.data) {
    throw new Error('No data returned from API');
  }

  return response.data;
}

export async function deleteJob(id: string) {
  const response = await api.delete<{ message: string }>(`/jobs/${id}`);

  if (!response.data) {
    throw new Error('No data returned from API');
  }

  return response;
}

export async function cancelJob(id: string, reason?: string) {
  const response = await api.post<Job>(`/jobs/${id}/cancel`, { reason });

  if (!response.data) {
    throw new Error('No data returned from API');
  }

  return response.data;
}

export async function addTasksToJob(id: string, data: AddTasksToJobInput) {
  const response = await api.post<{ newTasks: JobTask[]; newQuantity: number }>(
    `/jobs/${id}/tasks`,
    data
  );

  if (!response.data) {
    throw new Error('No data returned from API');
  }

  return response.data;
}

// ============================================================================
// TASKS
// ============================================================================

export async function listTasks(query: JobTaskQuery) {
  // Extract jobId from query and build remaining query params
  const { jobId, ...restQuery } = query;

  if (!jobId) {
    throw new Error('jobId is required');
  }

  const response = await api.get<JobTask[]>(
    `/jobs/${jobId}/tasks${buildQueryString(restQuery)}`
  );

  if (!response.data) {
    throw new Error('No data returned from API');
  }

  return { data: response.data, meta: response.meta };
}

export async function getTask(jobId: string, taskId: string) {
  const response = await api.get<JobTaskWithRelations>(`/jobs/${jobId}/tasks/${taskId}`);

  if (!response.data) {
    throw new Error('No data returned from API');
  }

  return response.data;
}

export async function updateTask(jobId: string, taskId: string, data: UpdateJobTaskInput) {
  const response = await api.patch<JobTask>(`/jobs/${jobId}/tasks/${taskId}`, data);

  if (!response.data) {
    throw new Error('No data returned from API');
  }

  return response.data;
}

export async function cancelTask(jobId: string, taskId: string, reason?: string) {
  const response = await api.post<JobTask>(
    `/jobs/${jobId}/tasks/${taskId}/cancel`,
    { reason }
  );

  if (!response.data) {
    throw new Error('No data returned from API');
  }

  return response.data;
}

// ============================================================================
// NOTES
// ============================================================================

export async function listJobNotes(jobId: string, query?: JobNotesQuery) {
  const response = await api.get<JobNoteWithRelations[]>(
    `/jobs/${jobId}/notes${buildQueryString(query)}`
  );

  if (!response.data) {
    throw new Error('No data returned from API');
  }

  return { data: response.data, meta: response.meta };
}

export async function createJobNote(jobId: string, data: CreateJobNoteInput) {
  const response = await api.post<JobNoteWithRelations>(`/jobs/${jobId}/notes`, data);

  if (!response.data) {
    throw new Error('No data returned from API');
  }

  return response.data;
}

export async function deleteJobNote(jobId: string, noteId: string) {
  const response = await api.delete<{ message: string }>(`/jobs/${jobId}/notes/${noteId}`);

  if (!response.data) {
    throw new Error('No data returned from API');
  }

  return response;
}

export async function listTaskNotes(jobId: string, taskId: string) {
  const response = await api.get<JobTaskNoteWithRelations[]>(
    `/jobs/${jobId}/tasks/${taskId}/notes`
  );

  if (!response.data) {
    throw new Error('No data returned from API');
  }

  return response.data;
}

export async function createTaskNote(
  jobId: string,
  taskId: string,
  data: CreateJobTaskNoteInput
) {
  const response = await api.post<JobTaskNoteWithRelations>(
    `/jobs/${jobId}/tasks/${taskId}/notes`,
    data
  );

  if (!response.data) {
    throw new Error('No data returned from API');
  }

  return response.data;
}

export async function deleteTaskNote(jobId: string, taskId: string, noteId: string) {
  const response = await api.delete<{ message: string }>(
    `/jobs/${jobId}/tasks/${taskId}/notes/${noteId}`
  );

  if (!response.data) {
    throw new Error('No data returned from API');
  }

  return response;
}

// ============================================================================
// CHANGE HISTORY
// ============================================================================

export async function getJobHistory(jobId: string, query?: JobHistoryQuery) {
  const response = await api.get<JobChangeHistoryWithRelations[]>(
    `/jobs/${jobId}/history${buildQueryString(query)}`
  );

  if (!response.data) {
    throw new Error('No data returned from API');
  }

  return { data: response.data, meta: response.meta };
}

export async function getTaskHistory(jobId: string, taskId: string) {
  const response = await api.get<JobTaskChangeHistoryWithRelations[]>(
    `/jobs/${jobId}/tasks/${taskId}/history`
  );

  if (!response.data) {
    throw new Error('No data returned from API');
  }

  return response.data;
}
